import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gtplSaathiAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Ticket, Send, CheckCircle, AlertCircle, Paperclip, X, Search, ChevronDown, ChevronUp, Loader, History } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Field, Notice, Pill, Item, Empty, Loading, Details } from '../components/gtpl/GtplKit';

const MAX_PHOTO_BYTES = 4 * 1024 * 1024;

// OPEN/CLOSE/IN PROGRESS/DETAILS REQUIRE — the exact 4 values gtplsaathi.com's own
// MyTicket.aspx uses (confirmed live 2026-08-27).
const STATUS_TONE = { OPEN: 'warn', CLOSE: 'ok', 'IN PROGRESS': 'info', 'DETAILS REQUIRE': 'bad' };
function StatusBadge({ status }) {
  return <Pill tone={STATUS_TONE[(status || '').toUpperCase()] || 'mute'} dot>{status || '—'}</Pill>;
}

// Inline expand-in-place detail for one ticket row — fetched on demand (each fetch is a real
// multi-step live scrape, see gtplSaathiAPI.getTicketDetail) and cached in the parent's
// `details` map so re-expanding an already-viewed row doesn't refetch.
function TicketDetailBody({ detail }) {
  const closed = detail.status?.toUpperCase() === 'CLOSE';
  return (
    <div style={{ marginTop: 10 }}>
      <Details items={[['Department', detail.department], ['Category', detail.category], ['Branch', detail.branch], ['Unit', detail.unit], ['Done by', detail.doneBy]]} />
      <div className="gx-hint" style={{ margin: '10px 0 2px' }}>Original remark</div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: '.88rem' }}>{detail.originalRemark || '—'}</div>
      {detail.replies?.length > 0 && (
        <>
          <div className="gx-hint" style={{ margin: '12px 0 4px' }}>{closed ? 'Resolution' : 'Response'}</div>
          <div className="gx-list">
            {detail.replies.map((r, i) => (
              <Item key={i} tone={closed ? 'ok' : 'info'} title={r.author || 'GTPL'} sub={r.time}
                meta={<span style={{ whiteSpace: 'pre-wrap' }}>{r.text || '—'}</span>} />
            ))}
          </div>
        </>
      )}
      {!detail.replies?.length && detail.status?.toUpperCase() === 'OPEN' && (
        <div className="gx-hint" style={{ marginTop: 8, fontStyle: 'italic' }}>No response yet — still open.</div>
      )}
    </div>
  );
}

function MyTicketsSection() {
  const [query, setQuery] = useState('');
  const [tickets, setTickets] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [expanded, setExpanded] = useState(null); // reqId currently expanded
  const [details, setDetails] = useState({});     // reqId -> detail (cache)
  const [loadingDetail, setLoadingDetail] = useState(null); // reqId currently fetching

  const runSearch = async (reqId) => {
    setLoadingList(true);
    setExpanded(null);
    try {
      const { data } = await gtplSaathiAPI.listTickets(reqId);
      setTickets(data.data || []);
      setSearched(true);
      if (reqId && !(data.data || []).length) toast.error(`No ticket found for Request Id ${reqId}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoadingList(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runSearch(query.trim() || undefined);
  };

  const toggleExpand = async (reqId) => {
    if (expanded === reqId) { setExpanded(null); return; }
    setExpanded(reqId);
    if (details[reqId]) return; // cached
    setLoadingDetail(reqId);
    try {
      const { data } = await gtplSaathiAPI.getTicketDetail(reqId);
      setDetails((prev) => ({ ...prev, [reqId]: data.data }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ticket detail');
      setExpanded(null);
    } finally {
      setLoadingDetail(null);
    }
  };

  return (
    <Panel icon={History} tone="info" title="My tickets" sub="Is a ticket still open or closed — and if closed, GTPL's reply.">
      <form onSubmit={handleSearchSubmit} className="gx-actions" style={{ alignItems: 'flex-end', marginBottom: 12 }}>
        <Field label="Request Id" style={{ flex: '1 1 180px' }}>
          <input className="form-control" placeholder="Search by Request Id…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </Field>
        <button type="submit" className="btn btn-primary" disabled={loadingList}>
          <Search size={15} />{loadingList ? 'Searching…' : 'Search'}
        </button>
        <button type="button" className="btn btn-secondary" disabled={loadingList} onClick={() => { setQuery(''); runSearch(); }}>
          Show recent
        </button>
      </form>

      {loadingList && <Loading>Loading — MyTicket.aspx is a large page, this can take a few seconds…</Loading>}

      {!loadingList && searched && tickets?.length === 0 && <Empty title="No tickets found" />}

      {!loadingList && tickets?.length > 0 && (
        <div className="gx-list">
          {tickets.map((t) => (
            <div key={t.reqId}>
              <button type="button" onClick={() => toggleExpand(t.reqId)} aria-expanded={expanded === t.reqId}
                style={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer', boxSizing: 'border-box' }}>
                <Item selected={expanded === t.reqId} title={`#${t.reqId}`} sub={t.ticketType}
                  right={<span className="gx-actions" style={{ gap: 8 }}>
                    <StatusBadge status={t.status} />
                    {loadingDetail === t.reqId
                      ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      : (expanded === t.reqId ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </span>}
                  meta={<span>{t.date}</span>} />
              </button>
              {expanded === t.reqId && details[t.reqId] && <TicketDetailBody detail={details[t.reqId]} />}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// Raises an account-level eSupport ticket to GTPL itself (gtplsaathi.com's Support.aspx) —
// not tied to any one customer, unlike the per-customer "Apply Order"/Suspend-Activate tab on
// CustomerDetailPage. See gtplSaathiService.raiseSupportTicket: on failure (session expired,
// network error, etc.) an admin is notified on WhatsApp automatically
// (gtpl_saathi_ticket_failed), same pattern as the Apply Order failure alert.
export default function GtplSaathiTicketPage() {
  useDarkReady();
  const [options, setOptions] = useState(null);
  const [department, setDepartment] = useState('');
  const [complaintType, setComplaintType] = useState('');
  const [remark, setRemark] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file after removing it
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files can be attached'); return; }
    if (file.size > MAX_PHOTO_BYTES) { toast.error('Photo is too large — must be under 4MB'); return; }
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview(null);
  };

  const loadOptions = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getSupportTicketOptions();
      setOptions(data.data);
      setDepartment(data.data.departments[0] || '');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load GTPL Saathi options');
    }
  }, []);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!complaintType) { toast.error('Select a ticket type'); return; }
    if (!remark.trim()) { toast.error('Enter a remark'); return; }
    setSubmitting(true);
    setResult(null);
    try {
      let payload;
      if (photo) {
        payload = new FormData();
        payload.append('department', department);
        payload.append('complaintType', complaintType);
        payload.append('remark', remark.trim());
        payload.append('photo', photo);
      } else {
        payload = { department, complaintType, remark: remark.trim() };
      }
      const { data } = await gtplSaathiAPI.raiseSupportTicket(payload);
      setResult({ ok: true, message: data.data?.message });
      toast.success('Ticket raised on GTPL Saathi');
      setComplaintType('');
      setRemark('');
      clearPhoto();
    } catch (err) {
      // No err.response means the request never got a reply at all (client timeout, network
      // drop) — the backend drives a real headless browser for this and keeps running even
      // after the client gives up, so "no response" is NOT proof the ticket wasn't raised
      // (confirmed live 2026-08-26: a client timeout here still produced a real GTPL ticket
      // a few seconds later). Only a genuine HTTP error response gets the "admin notified"
      // line — that alert only actually fires from the backend's own catch path.
      const unconfirmed = !err.response;
      const message = err.response?.data?.message
        || (unconfirmed ? 'No response from the server — the ticket may still have gone through on GTPL\'s side. Check GTPL Saathi → MyTicket before retrying, to avoid raising a duplicate.' : 'Failed to raise ticket');
      setResult({ ok: false, unconfirmed, message });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen narrow>
      <PageHead icon={Ticket} tone="info" title="GTPL eSupport"
        sub="Raise a support ticket directly to GTPL from your LCO account — for account/operations issues, not a specific customer's complaint." />

      <form onSubmit={handleSubmit}>
        <Panel icon={Send} tone="info" title="Raise a ticket">
          <div className="gx-form">
            <div className="gx-grid">
              <Field label="Department">
                <select className="form-control" value={department} onChange={e => setDepartment(e.target.value)}>
                  {(options?.departments || []).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <Field label="Ticket type">
                <select className="form-control" value={complaintType} onChange={e => setComplaintType(e.target.value)}>
                  <option value="">Select ticket type…</option>
                  {(options?.ticketTypes || []).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Remark">
              <textarea className="form-control" rows={4} placeholder="Describe the issue…" value={remark} onChange={e => setRemark(e.target.value)} />
            </Field>

            <Field label="Photo (optional)">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
              {photoPreview ? (
                <div className="gx-actions" style={{ flexWrap: 'nowrap' }}>
                  <img src={photoPreview} alt="Attachment preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                  <div className="gx-hint" style={{ flex: 1, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo?.name}</div>
                  <button type="button" className="btn btn-sm btn-danger" onClick={clearPhoto}><X size={14} />Remove</button>
                </div>
              ) : (
                <div><button type="button" className="btn btn-sm btn-secondary" onClick={() => fileInputRef.current?.click()}>
                  <Paperclip size={14} />Attach a photo
                </button></div>
              )}
            </Field>
          </div>

          <div className="gx-sticky">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              <Send size={15} />{submitting ? 'Raising…' : 'Raise ticket'}
            </button>
          </div>

          {result && (
            <Notice tone={result.ok === false ? (result.unconfirmed ? 'warn' : 'bad') : 'ok'}
              icon={result.ok === false ? AlertCircle : CheckCircle}
              title={result.ok === false ? (result.unconfirmed ? 'Unconfirmed' : 'Failed') : 'Submitted'}>
              {result.message}
              {/* The WhatsApp alert only actually fires from the backend's own failure path —
                  a client-side timeout/network drop (unconfirmed) never reaches it, so don't
                  claim it did. */}
              {result.ok === false && !result.unconfirmed && <div style={{ marginTop: 6 }}>An admin has been notified on WhatsApp.</div>}
            </Notice>
          )}
        </Panel>
      </form>

      <MyTicketsSection />
    </Screen>
  );
}
